import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useSession } from '../lib/auth-client'
import { t, tRico } from '../lib/i18n'
import { useTRPC } from '../lib/trpc'

export const Route = createFileRoute('/convite/$token')({ component: AcceptInvite })

/** Em português; traduzido na hora de desenhar, não na importação. */
function motivo(chave: string): string | undefined {
  const motivos: Record<string, string> = {
    inexistente: t('Este link de convite não existe.'),
    revogado: t('Este convite foi revogado por quem o criou.'),
    expirado: t('Este convite expirou.'),
    esgotado: t('Este convite já atingiu o limite de usos.'),
  }
  return motivos[chave]
}

function AcceptInvite() {
  const { token } = Route.useParams()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const session = useSession()

  const preview = useQuery(trpc.invite.preview.queryOptions({ token }))

  const accept = useMutation({
    ...trpc.invite.accept.mutationOptions(),
    onSuccess: async (result) => {
      // O layout escolhe o workspace pelo que está no localStorage; gravar
      // aqui evita cair no workspace antigo logo após aceitar.
      localStorage.setItem('tipscore:workspace', result.workspaceId)
      await queryClient.invalidateQueries({ queryKey: [['me']] })
      navigate({ to: '/inbox' })
    },
  })

  // Guarda o token para retomar depois do login: quem chega deslogado é
  // mandado para /login e voltaria sem saber de qual convite veio.
  useEffect(() => {
    if (!session.isPending && !session.data) {
      sessionStorage.setItem('tipscore:convite', token)
      navigate({ to: '/login' })
    }
  }, [session.isPending, session.data, token, navigate])

  /**
   * Com sessão e convite válido, aceita sozinho.
   *
   * A tela de confirmação era um passo sem escolha: quem abriu o link e fez
   * login já disse que quer entrar. Pior, quem já era membro via de novo o
   * "Aceitar convite", como se ainda estivesse de fora. O aceite é idempotente
   * no servidor — reabrir o link não gasta uso nem rebaixa papel.
   */
  const jaTentou = useRef(false)
  useEffect(() => {
    if (jaTentou.current) return
    if (!session.data || !preview.data?.valid) return

    jaTentou.current = true
    accept.mutate({ token })
  }, [session.data, preview.data, token, accept.mutate])

  if (preview.isPending || session.isPending) {
    return <Centered>{t('Verificando convite…')}</Centered>
  }

  if (!preview.data?.valid) {
    return (
      <Centered>
        <p className="font-display text-xl">{t('Convite indisponível')}</p>
        <p className="mt-2 text-sm text-ink-2">
          {motivo(preview.data?.reason ?? 'inexistente') ?? t('Este convite não pode ser usado.')}
        </p>
        <p className="mt-3 text-2xs text-ink-3">
          {t('Peça um link novo a quem administra o workspace.')}
        </p>
      </Centered>
    )
  }

  if (!session.data) return <Centered>{t('Redirecionando para o login…')}</Centered>

  return (
    <Centered>
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-3">{t('Convite')}</p>
      <h1 className="mt-2 font-display text-2xl">{preview.data.workspaceName}</h1>
      <p className="mt-2 text-sm text-ink-2">
        {tRico('Você foi convidado como {papel}.', {
          papel: (
            <strong>{preview.data.role === 'admin' ? t('administrador') : t('atendente')}</strong>
          ),
        })}
      </p>

      {/* O botão só reaparece se o aceite automático falhar — aí ele é uma
          segunda chance de verdade, e não um passo obrigatório. */}
      {accept.error ? (
        <>
          <p role="alert" className="mt-3 text-xs text-danger">
            {t(accept.error.message)}
          </p>
          <button
            type="button"
            onClick={() => accept.mutate({ token })}
            disabled={accept.isPending}
            className="mt-4 rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-on-accent disabled:opacity-50"
          >
            {accept.isPending ? t('Entrando…') : t('Tentar de novo')}
          </button>
        </>
      ) : (
        <p className="mt-5 text-sm text-ink-2">{t('Entrando…')}</p>
      )}

      <p className="mt-3 text-2xs text-ink-3">
        {t('Entrando como {email}', { email: session.data.user.email })}
      </p>
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="grid h-full place-items-center px-5 text-center">
      <div className="max-w-sm">{children}</div>
    </main>
  )
}
